import React from 'react';
import { motion } from 'framer-motion';
import MicRingMedium from '../assets/icons/VoiceConversation/mic-ring-medium.png';
import MicRingSmall from '../assets/icons/VoiceConversation/mic-ring-small.png';
import SignalBars from '../components/SignalBars';

interface MicAndVisualizerProps {
  aiIdle: boolean;
  aiListening: boolean;
  aiThinking: boolean;
  aiTalking: boolean;
  showMic: boolean;
  isConvoReady: boolean;
  onClickListening: (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => void;
  onClickInterrupt: (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => void;
  getIcon: () => JSX.Element | null;
}

const MicAndVisualizer: React.FC<MicAndVisualizerProps> = ({
  aiIdle,
  aiListening,
  aiThinking,
  aiTalking,
  showMic,
  isConvoReady,
  onClickListening,
  onClickInterrupt,
  getIcon,
}) => {
  if (!isConvoReady) return null;

  return (
    <div className="MicAndVisualizerContainer absolute bottom-0 md:bottom-8 flex w-full flex-col items-center justify-center font-poppins text-[20px] font-medium text-white">
      <div className="flex items-center justify-center">
        <div className="MicButtonContainer" role="presentation">
          <div className="flex h-[150px] w-[250px] items-center justify-center">
            {/* IDLE */}
            {aiIdle && showMic && (
              <div
                id="mic"
                onClick={(event) => onClickListening(event)}
                className="flex h-full w-full items-center justify-center"
              >
                <div className="absolute left-1/2 -translate-x-1/2 transform">
                  <motion.img
                    initial={{ width: 150, height: 150 }}
                    animate={{
                      width: [150, 100, 150],
                      height: [150, 100, 150],
                    }}
                    transition={{
                      duration: 10,
                      repeat: Infinity,
                      ease: 'linear',
                      times: [0, 4, 8, 12],
                    }}
                    src={MicRingMedium}
                    alt="mic"
                  />
                </div>
                <div className="absolute left-1/2 -translate-x-1/2 transform">
                  <motion.img
                    initial={{ width: 100, height: 100 }}
                    animate={{
                      width: [100, 50, 100],
                      height: [100, 50, 100],
                    }}
                    transition={{
                      duration: 10,
                      repeat: Infinity,
                      ease: 'linear',
                      times: [0, 4, 8, 12],
                    }}
                    src={MicRingSmall}
                    alt="mic"
                  />
                </div>
              </div>
            )}

            {/* LISTENING */}
            {aiListening && (
              <div
                className="flex h-3/5 w-full justify-center"
                onClick={(event) => onClickListening(event)}
              >
                <SignalBars />
              </div>
            )}

            {/* IDLE - Main Mic Button */}
            {aiIdle && (
              <motion.div
                className="absolute left-1/2 flex h-[95px] w-[95px] -translate-x-1/2 transform cursor-pointer items-center justify-center rounded-full bg-white"
                onClick={(event) => onClickListening(event)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onClickListening(e as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>)}
              >
                <span>{getIcon()}</span>
              </motion.div>
            )}

            {/* TALKING - Interrupt Button */}
            {(aiTalking || aiThinking) && (
              <motion.div
                className="absolute left-1/2 flex h-[95px] w-[95px] -translate-x-1/2 transform cursor-pointer items-center justify-center rounded-full bg-white"
                onClick={(event) => onClickInterrupt(event)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onClickInterrupt(e as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>)}
              >
                <span>{getIcon()}</span>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MicAndVisualizer;
